import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useRazorpay from "../../../components/useRazorpay/useRazorpay";
import "./styles.css";
import { API_BASE_URL } from "../../../config/baseUrls";

const validateAnswers = (questions, sharedAnswers) => {
  const newErrors = {};

  questions?.forEach((q) => {
    const answer = sharedAnswers[q.id];

    if (
      q.isRequired &&
      (!answer ||
        (typeof answer === "string" && answer.trim() === "") ||
        (Array.isArray(answer) && answer.length === 0))
    ) {
      newErrors[q.id] = `This field is required.`;
      return;
    }

    if (!q.isRequired && (!answer || answer === "")) return;

    switch (q.questionType) {
      case "EMAIL":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answer))
          newErrors[q.id] = `Invalid email format.`;
        break;
      case "PHONE":
        const phoneDigits = String(answer).replace(/[^0-9]/g, "");
        if (phoneDigits.length !== 10)
          newErrors[q.id] = `Phone number must be 10 digits.`;
        break;
      case "TEXT":
      case "TEXTAREA":
        const ansLength = (answer || "").length;
        if (q.minLength && ansLength < q.minLength)
          newErrors[q.id] = `Answer minimum length: ${q.minLength}.`;
        if (q.maxLength && ansLength > q.maxLength)
          newErrors[q.id] = `Answer maximum length: ${q.maxLength}.`;
        break;
      default:
        break;
    }
  });

  return newErrors;
};

const EventCheckout = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("JWTtoken");
  const userId = localStorage.getItem("userId");
  const eventId = localStorage.getItem("eventId");
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [maxAllowedQuantity, setMaxAllowedQuantity] = useState(1);

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [orderOrReserveId, setOrderOrReserveId] = useState(null);

  const [sharedAnswers, setSharedAnswers] = useState({});
  const [answerErrors, setAnswerErrors] = useState({});

  const [userTickets, setUserTickets] = useState([]);
  const [canProceed, setCanProceed] = useState(true);
  const [disableMessage, setDisableMessage] = useState("");

  useRazorpay({
    onLoad: () => setIsRazorpayLoaded(true),
  });

  useEffect(() => {
    if (appliedCoupon) {
      setCouponCode("");
      setAppliedCoupon(null);
      setCouponError(null);
    }
  }, [quantity]);

  const handlePaymentSuccess = async (response, orderId) => {
    if (!orderId) {
      alert("Order ID missing for payment verification.");
      setIsProcessingPayment(false);
      return;
    }
    console.log("Razorpay Success Response:", response);
    try {
      const verifyResponse = await fetch(
        `${API_BASE_URL}/tickets/verify-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ticketId: orderId,
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          }),
        }
      );

      if (!verifyResponse.ok) {
        throw new Error("Payment verification failed.");
      }

      alert("Payment Successful & Verified!");
      navigate("/events/order-confirmed");
    } catch (err) {
      alert(`Payment verification failed: ${err.message}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentFailure = async () => {
    alert("Payment failed or was cancelled.");
    try {
      await fetch(
        `${API_BASE_URL}/tickets/payment-failed`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ticketId: orderOrReserveId,
            reason: "PAYMENT_FAILED_OR_CANCELLED",
          }),
        }
      );
    } catch (err) {
      console.error("Failed to log payment failure:", err);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const validateUserTickets = (tickets, eventData) => {
    const isBookingFlow = eventData.isBookingFlow === 1;
    const maxLimit = eventData.maxLimit || 999;

    const validTicketsCount = tickets.filter((ticket) => {
      if (ticket.status === "CONFIRMED") return true;
      if (isBookingFlow && ticket.status === "RESERVED") return true;
      return false;
    }).length;

    const remaining = maxLimit - validTicketsCount;

    if (remaining <= 0) {
      setCanProceed(false);
      setDisableMessage(
        `You have reached the maximum purchase limit (${maxLimit}) for this event.`
      );
      setMaxAllowedQuantity(0);
      return false;
    }

    setMaxAllowedQuantity(remaining);

    if (quantity > remaining) {
      setQuantity(remaining);
    }

    setCanProceed(true);
    setDisableMessage("");
    return true;
  };

  const fetchEvent = async (eventId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/products/${eventId}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch event details");
      }

      const result = await response.json();
      const productData = result.data.product;
      setEventData(productData);

      if (productData.maxLimit) {
        setMaxAllowedQuantity(productData.maxLimit);
      }

      const ticketsResponse = await fetch(
        `${API_BASE_URL}/tickets/userTickets/${userId}/${eventId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (ticketsResponse.ok) {
        const ticketsResult = await ticketsResponse.json();
        const tickets = ticketsResult.data || [];
        setUserTickets(tickets);

        validateUserTickets(tickets, productData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchEvent(eventId);
    }
  }, [eventId]);

  const handleApplyCoupon = async () => {
    if (!couponCode) return;

    setIsValidatingCoupon(true);
    setCouponError(null);
    setAppliedCoupon(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/tickets/validate-coupon`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            couponCode: couponCode,
            productId: eventId,
            quantity: quantity,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.data?.valid) {
        throw new Error(result.error || "This coupon is not valid.");
      }

      setAppliedCoupon(result.data);
      alert(`Coupon ${result.data.coupon.code} applied successfully!`);
    } catch (err) {
      setCouponError(err.message);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleContinue = async () => {
    const phoneRegex = /^\d{10}$/;
    const source = localStorage.getItem("ticket_metadata_source");
    if (!phoneNumber || !phoneRegex.test(phoneNumber)) {
      alert("Please enter a valid 10-digit phone number");
      return;
    }

    const errors = validateAnswers(eventData.questions, sharedAnswers);
    if (Object.keys(errors).length > 0) {
      setAnswerErrors(errors);
      alert("Please correct the errors in the required fields.");
      return;
    }
    setAnswerErrors({});

    if (!isRazorpayLoaded) {
      alert("Payment gateway is still loading, please wait a moment.");
      return;
    }

    setIsProcessingPayment(true);

    try {
      const isBookingFlow = eventData.isBookingFlow === 1;
      const endpoint = isBookingFlow
        ? `${API_BASE_URL}/tickets/reserve`
        : `${API_BASE_URL}/tickets/buy`;

      const questionAnswers = Object.keys(sharedAnswers).map((key) => ({
        questionId: key,
        answer: sharedAnswers[key],
      }));

      const formattedTicketsData = Array.from({ length: quantity }, () => {
        const ticketPayload = {
          questionAnswers,
          pointsUsed: 0,
        };
        if (appliedCoupon && appliedCoupon.coupon.code) {
          ticketPayload.couponCode = appliedCoupon.coupon.code;
        }
        return ticketPayload;
      });

      const requestBody = {
        userId: userId,
        productId: eventId,
        phoneNumber: phoneNumber,
        ticketsData: formattedTicketsData,
        metadata: source ? { source: source, campaign: "NTT_DATA" } : null,
      };

      console.log("API Payload:", requestBody);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to create order.");
      }

      const { data } = result;

      const mainOrderIdentifier = data.orderId;

      setOrderOrReserveId(mainOrderIdentifier);

      if (isBookingFlow) {
        alert("🎉 Ticket Reserved Successfully!");
        setIsProcessingPayment(false);
        navigate(`/events/order-confirmed`);
        return;
      }

      if (!data.needsPayment) {
        alert("🎉 Free Ticket Confirmed, Enjoy the event!");
        setIsProcessingPayment(false);
        navigate(`/events/order-confirmed`);
        return;
      }

      const razorpayInfo = data.razorpay;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: razorpayInfo.amount,
        currency: "INR",
        name: "Ōta",
        description: eventData.title,
        order_id: razorpayInfo.orderId,
        handler: (response) =>
          handlePaymentSuccess(response, mainOrderIdentifier),
        prefill: {
          contact: phoneNumber,
        },
        theme: {
          color: "#484EFD",
        },
        modal: {
          ondismiss: handlePaymentFailure,
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert(`Error placing order: ${err.message}`);
      setIsProcessingPayment(false);
    }
  };

  const handleDisabledClick = () => {
    if (!canProceed) {
      alert(disableMessage);
    }
  };

  const getPricing = () => {
    const defaultPricing = {
      originalPrice: eventData.pricing.unitPrice * quantity,
      couponDiscount: 0,
      priceAfterCoupon: eventData.pricing.unitPrice * quantity,
      taxes: eventData.pricing.taxAmount * quantity,
      fees: 0,
      total:
        eventData.pricing.unitPrice * quantity +
        eventData.pricing.taxAmount * quantity,
    };

    if (appliedCoupon) {
      const total =
        appliedCoupon.pricing.total * quantity -
        (appliedCoupon.pricing.pointsUsed * quantity) / 100;

      return {
        originalPrice: appliedCoupon.pricing.originalPrice * quantity,
        couponDiscount: appliedCoupon.pricing.couponDiscount * quantity,
        priceAfterCoupon: appliedCoupon.pricing.priceAfterCoupon * quantity,
        taxes: appliedCoupon.pricing.taxes * quantity,
        fees: appliedCoupon.pricing.fees * quantity,
        total: total,
      };
    }

    return defaultPricing;
  };

  const pricing = eventData ? getPricing() : {};
  const calculateTotal = () => (pricing.total || 0).toFixed(2);

  if (loading) {
    return (
      <div className="checkout-container loading-state">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="checkout-container error-state">{error}</div>;
  }

  if (!eventData) return null;

  const QuantitySelector = ({ quantity, setQuantity, maxAllowedQuantity }) => (
    <div className="quantity-control-group">
      <span className="quantity-label">Quantity:</span>
      <div className="quantity-stepper">
        {/* Minus Button */}
        <button
          onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
          disabled={quantity <= 1}
          className="stepper-btn minus-btn"
        >
          −
        </button>

        {/* Quantity Display */}
        <span className="stepper-value">{quantity}</span>

        {/* Plus Button */}
        <button
          onClick={() =>
            setQuantity((prev) => Math.min(maxAllowedQuantity, prev + 1))
          }
          disabled={quantity >= maxAllowedQuantity}
          className="stepper-btn plus-btn"
        >
          +
        </button>
      </div>
    </div>
  );

  const QuestionRenderer = ({
    questions,
    sharedAnswers,
    setSharedAnswers,
    errors,
  }) => (
    <section
      className="p-6 bg-white rounded-xl shadow-lg space-y-8"
      aria-labelledby="ticket-info-title"
    >
      <h2
        className="text-2xl font-bold text-gray-800 border-b pb-3 mb-4"
        id="ticket-info-title"
      >
        🎫 Ticket Information
      </h2>

      {questions.map((q, qIndex) => {
        const questionId = `question-${q.id}`;
        const errorId = `error-${q.id}`;
        const hasError = !!errors[q.id];

        const handleInputChange = (e) => {
          setSharedAnswers((prev) => ({
            ...prev,
            [q.id]: e.target.value,
          }));
        };

        return (
          <div key={q.id} className="space-y-2 pb-4 border-b last:border-b-0">
            {/* Label for the question */}
            <label
              htmlFor={questionId}
              className="block text-sm font-medium text-gray-700"
            >
              <span className="font-semibold text-base mr-1">
                {qIndex + 1}.
              </span>{" "}
              {q.question}{" "}
              {q.isRequired && (
                <span className="text-red-500 font-bold" aria-hidden="true">
                  *
                </span>
              )}
            </label>

            {/* 1. TEXT / EMAIL / PHONE / TEXTAREA Inputs */}
            {(q.questionType === "TEXT" ||
              q.questionType === "EMAIL" ||
              q.questionType === "PHONE" ||
              q.questionType === "TEXTAREA") &&
              (q.questionType === "TEXTAREA" ? (
                <textarea
                  id={questionId}
                  className={`w-full p-3 border rounded-lg text-gray-900 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out ${
                    hasError
                      ? "border-red-500 ring-1 ring-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder={q.placeholder || "Enter your detailed answer"}
                  value={sharedAnswers[q.id] || ""}
                  onChange={handleInputChange}
                  rows={4}
                  aria-invalid={hasError ? "true" : "false"}
                  aria-describedby={hasError ? errorId : undefined}
                />
              ) : (
                <input
                  id={questionId}
                  type={
                    q.questionType === "EMAIL"
                      ? "email"
                      : q.questionType === "PHONE"
                      ? "tel"
                      : "text"
                  }
                  className={`w-full p-3 border rounded-lg text-gray-900 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out ${
                    hasError
                      ? "border-red-500 ring-1 ring-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder={q.placeholder || "Enter your answer"}
                  value={sharedAnswers[q.id] || ""}
                  onChange={handleInputChange}
                  aria-invalid={hasError ? "true" : "false"}
                  aria-describedby={hasError ? errorId : undefined}
                />
              ))}

            {/* 2. SINGLE_CHOICE (Dropdown/Select) */}
            {q.questionType === "SINGLE_CHOICE" && (
              <select
                id={questionId}
                className={`w-full p-3 border rounded-lg appearance-none bg-white text-gray-900 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out ${
                  hasError
                    ? "border-red-500 ring-1 ring-red-500 bg-red-50"
                    : "border-gray-300"
                }`}
                value={sharedAnswers[q.id] || ""}
                onChange={handleInputChange}
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? errorId : undefined}
              >
                <option value="" disabled>
                  -- Select an option --
                </option>
                {q.options?.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}

            {/* 3. MULTIPLE_CHOICE (Checkboxes) */}
            {q.questionType === "MULTIPLE_CHOICE" && (
              <div
                className="option-group space-y-2 pt-1"
                role="group"
                aria-labelledby={questionId}
                aria-invalid={hasError ? "true" : "false"}
                aria-describedby={hasError ? errorId : undefined}
              >
                {q.options?.map((opt) => (
                  <div key={opt} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`${questionId}-${opt}`}
                      name={questionId}
                      value={opt}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      checked={
                        Array.isArray(sharedAnswers[q.id]) &&
                        sharedAnswers[q.id].includes(opt)
                      }
                      onChange={(e) => {
                        const currentAnswers = Array.isArray(
                          sharedAnswers[q.id]
                        )
                          ? sharedAnswers[q.id]
                          : [];

                        const newAnswer = e.target.checked
                          ? [...currentAnswers, opt]
                          : currentAnswers.filter((item) => item !== opt);

                        setSharedAnswers((prev) => ({
                          ...prev,
                          [q.id]: newAnswer,
                        }));
                      }}
                    />
                    <label
                      htmlFor={`${questionId}-${opt}`}
                      className="ml-2 text-sm text-gray-700 cursor-pointer"
                    >
                      {opt}
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* Error Message */}
            {hasError && (
              <p
                id={errorId}
                className="text-sm text-red-600 mt-1 flex items-center"
                role="alert"
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors[q.id]}
              </p>
            )}
          </div>
        );
      })}
    </section>
  );
  return (
    <div className="checkout-container">
      {/* Header */}
      <header className="checkout-header">
        <button
          className="back-button"
          onClick={() => navigate(`/events/${eventId}`)}
        >
          {/* ... SVG ... */}
        </button>
        <h1>Checkout</h1>
      </header>

      <div className="checkout-content">
        {/* Section 1: Phone Number */}
        <section className="checkout-section">
          <h2 className="section-title">Enter Phone Number</h2>
          <input
            type="tel"
            className="checkout-input"
            placeholder="Enter your phone number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </section>

        <hr />

        {/* Section 2: Custom Questions (New) */}
        {eventData.questions && eventData.questions.length > 0 && (
          <QuestionRenderer
            questions={eventData.questions}
            sharedAnswers={sharedAnswers}
            setSharedAnswers={setSharedAnswers}
            errors={answerErrors}
          />
        )}

        <hr />

        {/* Section 3: Coupon Code (Quantity sensitive now) */}
        <section className="checkout-section">
          <h2 className="section-title">Coupon Code ({quantity} Tickets)</h2>
          <div className="coupon-group">
            <input
              type="text"
              className="checkout-input coupon-input"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            />
            <button
              className="apply-button"
              onClick={handleApplyCoupon}
              disabled={isValidatingCoupon}
            >
              {isValidatingCoupon ? "..." : "Apply"}
            </button>
          </div>
          {couponError && <p className="coupon-message error">{couponError}</p>}
          {appliedCoupon && (
            <p className="coupon-message success">✓ Coupon Code applied!</p>
          )}
        </section>

        <hr />

        {/* Section 4: Order Summary */}
        <section className="checkout-section">
          <h2 className="section-title">Order Summary</h2>
          <div className="summary-card">
            {/* Product Info Row */}
            <div className="product-row">
              <img
                src={eventData.primaryImage}
                alt={eventData.title}
                className="product-thumb"
              />
              <div className="product-info">
                <h3 className="product-title">{eventData.title}</h3>
                <p className="product-price">
                  INR {eventData.pricing.unitPrice.toFixed(2)} / ticket
                </p>
                {/* Quantity Selector (New) */}
                <QuantitySelector
                  quantity={quantity}
                  setQuantity={setQuantity}
                  maxAllowedQuantity={maxAllowedQuantity}
                />
              </div>
            </div>

            <div className="divider"></div>

            {/* Price Breakdown */}
            <div className="price-breakdown">
              <div className="price-row">
                <span>Subtotal</span>
                <span>INR {pricing.originalPrice.toFixed(2)}</span>
              </div>

              {appliedCoupon && (
                <>
                  <div className="price-row discount">
                    <span>Coupon Discount</span>
                    <span>- INR {pricing.couponDiscount.toFixed(2)}</span>
                  </div>
                  <div className="price-row">
                    <span>Price After Discount</span>
                    <span>INR {pricing.priceAfterCoupon.toFixed(2)}</span>
                  </div>
                </>
              )}

              <div className="price-row">
                <span>Fees</span>{" "}
                {/* Renamed from Shipping Cost to match RN event logic */}
                <span>INR {pricing.fees.toFixed(2)}</span>
              </div>
              <div className="price-row">
                <span>Taxes</span>
                <span>INR {pricing.taxes.toFixed(2)}</span>
              </div>
              <div className="price-row total">
                <span>Grand Total</span>
                <span>INR {calculateTotal()}</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Sticky Footer */}
      <footer className="checkout-footer">
        <div className="footer-total">
          <span className="total-qty">{quantity} Qty</span>
          <span className="total-amount">
            INR {eventData ? calculateTotal() : "0.00"}
          </span>
        </div>

        {canProceed && quantity > 0 ? (
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!isRazorpayLoaded || isProcessingPayment || quantity < 1}
          >
            {isProcessingPayment ? "Processing..." : "Continue"}
          </button>
        ) : (
          <div className="button-wrapper" onClick={handleDisabledClick}>
            <div className="continue-button-disabled">Continue</div>
            {disableMessage && (
              <span className="tooltip-text">{disableMessage}</span>
            )}
          </div>
        )}
      </footer>
    </div>
  );
};

export default EventCheckout;
